import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { useSsrElementsBank } from 'shared/hooks';
import {
  getSSRsRatingListService,
  getSSRsSwitchSizesService,
  getZonesInfoForSystemIdentificationService,
} from 'features/settings/services/systemIdentification.service';
import {
  handleLocationsSystemIdentification,
  handleSSRRating,
  handleSwitchSize,
  selectSysIdentification,
} from 'features/settings/slice/admin/sysIdentificationSlice';
import {
  alignItemsFlexStart,
  flexBoxCenter,
  justifyContentFlexEnd,
  justifyContentFlexStart,
  justifyContentSpaceAround,
  justifyContentSpaceBetween,
  justifyContentSpaceEvenly,
  layerA,
  layerA180Deg,
  layerADark,
  layerB,
  layerCLighter,
} from 'shared/styles/commonStyles';
import SystemIdentificationChooser from './SystemIdentificationChooser';
import SelectBox from './SelectBox';
import SSRElements from './SSRElements';
import SettingClearOkMessage from '../../messageBoxes/SettingClearOkMessage';
import FakeSelectBox from './FakeSelectBox';
import AutoCompletePlaces from './AutoCompletePlaces';
import { useRef } from 'react';
import DeleteSwitchMessageBox from '../../messageBoxes/DeleteSwitchMessageBox';
import ErrorMessageBox from '../../messageBoxes/ErrorMessageBox';
import SettingConfirmCancelMessageBox from '../../messageBoxes/SettingConfirmCancelMessageBox';
import ConfirmedUOSInfo from './ConfirmedUOSInfo';
import InitialUOSInfo from './InitialUOSInfo';
import {
  availableUOSPanels,
  applyHeatingSystemSSRRules,
  areSwitchRowCountsUsable,
  collectConfirmProblems,
  derivePanelCounts,
  formatUOSNumber,
  isSysIdFormDirty,
  makeBlankSwitch,
  reassignSwitchUOS,
  resolvePanelType,
  resizeSwitches,
  shouldDeleteLocation,
  snapshotSysIdForm,
  SYS_ID_MODES,
} from 'features/settings/helpers/setting';
// import { is } from 'immer/dist/internal.js';

// AdminMain's handleConfirmOrSave switches on a settings-section id.
// 3 = system identification. Unrelated to any button position in this file.
const SYS_ID_SECTION = 3;

const SystemIdentification = ({
  isCreateEditOrSave,
  sysIdMode,
  setSysIdMode,
  locationNameRef,
  civicAddress,
  setCivicAddress,
  specificLocation,
  setSpecificLocation,
  switches,
  setSwitches,
  handleConfirmOrSave,
  selectedLocation,
  setSelectedLocation,
  numSwitches,
  setNumSwitches,
  numOfUOS,
  setNumOfUOS,
  numOfSSR,
  setNumOfSSR,
  UOSName,
  setUOSName,
  UOSDelete,
  setUOSDelete,
  isGroupedSwitchesSaved,
  setIsGroupedSwitchesSaved,
  isConfirmed,
  setIsConfirmed,
  // specificLocationRef,
}) => {
  // create/edit moved to the chooser; only in-form actions remain.
  const buttonTitles = ['cancel', 'confirm', 'save'];

  // useState
  const [listOfUOS, setListOfOUS] = useState([]);
  const [listOfSelectedSSR, setListOfSelectedSSR] = useState([]);
  // const [listOfGasType, setListOfGasType] = useState([]);
  const [initialStart, setInitialStart] = useState(false);
  const [expandHeatLoad, setExpandHeatLoad] = useState([]);
  const [temporaryGreenCircle, setTemporaryGreenCircle] = useState(false);
  const [openMessageBox, setOpenMessageBox] = useState(false);
  const [saveGasTypeTemporary, setSaveGasTypeTemporary] = useState({});
  const [saveSSRRatingTemporary, setSaveSSRRatingTemporary] = useState({});
  const [saveHeatingSysTemporary, setSaveHeatingSysTemporary] = useState({});
  const [saveSSRSelectedTemporary, setSaveSSRSelectedTemporary] = useState({});
  const [shortCutOfLocationName, setShortCutOfLocationName] = useState('');
  const [deleteSwitch, setDeleteSwitch] = useState(false);
  const [incorrectLocationNameFormat, setIncorrectLocationNameFormat] =
    useState(false);
  const [confirmProblems, setConfirmProblems] = useState([]);
  // transit-station mode is disabled: the /zones payload does not accept the
  // zone name / segment fields yet, so the toggle was removed rather than let
  // users type data that is silently discarded on save. selectSystem stays
  // false and its conditionals below are dormant until the backend catches up.
  // Re-enable by restoring <SelectSystem /> — see SelectSystem.js.
  const [selectSystem, setSelectSystem] = useState(false);

  const civicAddressRef = useRef();

  // Captured on entering a mode; compared on cancel to decide whether to warn.
  const formSnapshotRef = useRef(null);

  // redux
  const dispatch = useDispatch();
  const SysIdentificationState = useSelector(selectSysIdentification);
  const {
    locations,
    heatingSysOptions,
    switchSizeOptions,
    ssrRatingOptions,
    gasTypeOptions,
  } = SysIdentificationState;
  const { elementsOptions } = useSsrElementsBank();

  // handles creating the list of array[] of number of UOS, number of SSR UTS drop down list
  const handleCreateUOSAndSSRList = useCallback(
    (UOS, SSR) => {
      const createNumOfUOSArr = [];
      const createNumOfSSRArr = [];
      for (let i = 1; i <= UOS; i++) {
        createNumOfUOSArr.push(formatUOSNumber(i));
      }
      for (let i = 1; i <= SSR; i++) {
        createNumOfSSRArr.push('ssr' + i);
      }
      setListOfOUS(createNumOfUOSArr);
      setListOfSelectedSSR(createNumOfSSRArr);
    },
    [numOfUOS, numOfSSR],
  );

  useEffect(() => {
    const copySwitches = [...switches];
    let sysHeating = {};
    let sysSSRRating = {};
    let sysSSRSelected = {};
    copySwitches.forEach((switches) => {
      sysHeating[switches.UOS] = Array.isArray(sysHeating[switches.UOS])
        ? [...sysHeating[switches.UOS], switches.heatingSys]
        : [switches.heatingSys];
      sysSSRRating[switches.UOS] = Array.isArray(sysSSRRating[switches.UOS])
        ? [...sysSSRRating[switches.UOS], switches.ssrRating]
        : [switches.ssrRating];
      sysSSRSelected[switches.UOS] = Array.isArray(sysSSRSelected[switches.UOS])
        ? [...sysSSRSelected[switches.UOS], switches.selectedSSR]
        : [switches.selectedSSR];
    });

    setSaveHeatingSysTemporary(sysHeating);
    setSaveSSRRatingTemporary(sysSSRRating);
    setSaveSSRSelectedTemporary(sysSSRSelected);
  }, [numSwitches]);

  useEffect(() => {
    getSSRsSwitchSizesService().then((res) => {
      dispatch(handleSwitchSize(res));
    });
    getSSRsRatingListService().then((res) => {
      dispatch(handleSSRRating(res));
    });
    return () => {
      setSaveGasTypeTemporary({});
      setSaveSSRRatingTemporary({});
      setSaveHeatingSysTemporary({});
      setSaveSSRSelectedTemporary({});
    };
  }, []);

  useEffect(() => {
    getZonesInfoForSystemIdentificationService({
      structured: true,
    }).then((res) => {
      dispatch(
        handleLocationsSystemIdentification({
          data: res,
          heaterSpecs: elementsOptions,
        }),
      );
    });
  }, [elementsOptions]);

  useEffect(() => {
    setInitialStart(true);
    setSwitches([makeBlankSwitch()]);
    return () => {
      setSysIdMode(null);
      setIsConfirmed(false);
    };
  }, []);

  // Called directly by the chooser once a location is picked, so the form
  // populates in one pass. It used to be an effect watching selectedLocation,
  // which meant the fields blanked first and refilled a render later.
  const populateFromLocation = (selection) => {
    const location = locations[selection.locationIdx];

    // One decision, made once. The stored counts and the switch list used to be
    // read under two subtly different conditions — one also checked that
    // specificLocation had keys, the other did not — so a sub-location could be
    // picked for the counts and missed for the switches, or the reverse.
    const record =
      selection?.specificLocation &&
      Object.keys(location?.specificLocation ?? {}).length > 0
        ? location.specificLocation[selection.specificLocation]
        : location;

    setCivicAddress(location.address ?? '');
    // location.specificLocation is a *map* of sub-locations on a parent and a
    // plain string on a location without them. Only the string belongs in the
    // field; the map would render as "[object Object]".
    const ownSpecificLocation =
      typeof location.specificLocation === 'string'
        ? location.specificLocation
        : '';
    setSpecificLocation(selection.specificLocation || ownSpecificLocation);

    // to extract the shortcut of location name
    setShortCutOfLocationName((selection.location ?? '').split('-')[2] ?? '');

    const copyLocations = JSON.parse(
      JSON.stringify(record?.switchInfo ?? []),
    );
    setSwitches(copyLocations);

    // The stored uos_panel_number and ssr_quantity arrive as null when the
    // server has none, which left both dropdowns empty on a site whose switch
    // rows plainly show UOS 01, 02. Raise them to what the switches prove.
    const { numOfUOS: uosCount, numOfSSR: ssrCount } = derivePanelCounts(
      copyLocations,
      record?.numOfUOS,
      record?.numOfSSR,
    );
    setNumOfUOS(uosCount);
    setNumOfSSR(ssrCount);
    // creating the list of array[] of number of UOS, number of SSR UTS drop down list
    handleCreateUOSAndSSRList(uosCount, ssrCount);

    // Count the switch panels that actually came back, rather than trusting the
    // stored switches_number. They are derived independently — the panels by
    // flattening uos_panels[].switch_panels[], the number from a separate server
    // scalar — so they can disagree. When the scalar was the smaller of the two,
    // the resize effect below truncated real switches out of the form, and the
    // truncated list was what the next save persisted.
    setNumSwitches(copyLocations.length);
  };

  // Mirrors the field reset in handleButtons so add mode never inherits a
  // previous session's values. Kept self-sufficient rather than relying on a
  // caller to clear first, the way handleEnterEdit overwrites everything it needs.
  const handleEnterAdd = () => {
    setSwitches([makeBlankSwitch()]);
    setNumOfUOS('');
    setNumOfSSR('');
    setNumSwitches('');
    setCivicAddress('');
    setSpecificLocation('');
    setSysIdMode(SYS_ID_MODES.ADD);
    setIsConfirmed(false);
    setSelectedLocation(null);
    resetConfirmState();
  };

  const handleEnterEdit = (selection) => {
    setSysIdMode(SYS_ID_MODES.EDIT);
    setIsConfirmed(false);
    setSelectedLocation(selection);
    populateFromLocation(selection);
    resetConfirmState();
  };

  // Everything the confirm step produces. It lives in SettingsMain, so it
  // outlives this component and would otherwise leak into the next session:
  // a stale non-empty isGroupedSwitchesSaved makes save-before-confirm take
  // the "a save happened" branch and falsely clear the dirty guard.
  // Called on entry as well as exit because exit is not the only way back to
  // the chooser — the unmount cleanup below and SettingsMain's section-level
  // cancel both null sysIdMode without passing through exitToChooser, while
  // entering a mode is the one funnel every session must go through.
  // Safe to blank: the only reader of UOSName/expandHeatLoad is
  // ConfirmedUOSInfo, rendered solely under isConfirmed, which every caller
  // sets false in the same batch.
  const resetConfirmState = () => {
    setIsGroupedSwitchesSaved([]);
    setUOSName([]);
    setExpandHeatLoad([]);
  };

  const readForm = () => ({
    locationName: locationNameRef.current?.value ?? '',
    specificLocation,
    civicAddress,
    numOfUOS,
    numOfSSR,
    numSwitches,
    switches,
  });

  const refreshSnapshot = () => {
    formSnapshotRef.current = snapshotSysIdForm(readForm());
  };

  // Entering a mode queues a batch of setState calls; this effect runs after
  // they have been applied, so it snapshots what the user actually sees.
  // Must stay declared ABOVE the handleNumOfSwitches effect below: that one
  // calls setSwitches on entering edit, and the snapshot must be taken first.
  useEffect(() => {
    if (sysIdMode === null) return;
    refreshSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sysIdMode]);

  useEffect(() => {
    if (
      areSwitchRowCountsUsable({ numOfUOS, numOfSSR, numSwitches })
    ) {
      handleNumOfSwitches();
    }
  }, [numOfSSR, numOfUOS, numSwitches]);

  // handles on  keydown 'enter'. it creates or delete switches depending the number entered at the input box of number of switches
  const handleNumOfSwitches = useCallback(() => {
    // Create
    if (isCreateEditOrSave === 0) {
      if (numSwitches === 0) {
        setSwitches([]);
      } else if (
        areSwitchRowCountsUsable({ numOfUOS, numOfSSR, numSwitches })
      ) {
        handleCreateUOSAndSSRList(numOfUOS, numOfSSR);
        setSwitches(resizeSwitches(switches, numSwitches, makeBlankSwitch));
      } else {
        throw new Error('Please enter # of UOS Panels and SSR QTY.');
      }
    }
    // Edit
    if (isCreateEditOrSave === 1) {
      let copySwitches = [];
      if (switches.length === 0) {
        if (selectedLocation?.specificLocation) {
          const switchInfoArr =
            locations[selectedLocation.locationIdx].specificLocation[
              selectedLocation.specificLocation
            ].switchInfo;
          if (switchInfoArr.length === 0) {
            copySwitches.push(makeBlankSwitch());
          } else {
            copySwitches = JSON.parse(JSON.stringify(switchInfoArr));
          }
        } else {
          if (
            locations[selectedLocation.locationIdx].switchInfo?.length === 0
          ) {
            copySwitches.push(makeBlankSwitch());
          } else {
            copySwitches = JSON.parse(
              JSON.stringify(
                locations[selectedLocation.locationIdx].switchInfo,
              ),
            );
          }
        }

        setSwitches(copySwitches);
      } else {
        setSwitches(resizeSwitches(switches, numSwitches, makeBlankSwitch));

        handleCreateUOSAndSSRList(numOfUOS, numOfSSR);
      }
    }
  }, [numOfSSR, numOfUOS, numSwitches]);

  // open or close the drop down select boxes of switch size, heating system and SSR Rating
  const handleOpenSelectBoxes = (switchIdx, displaySelectBoxIdx) => {
    const copySwitches = [...switches];
    copySwitches[switchIdx].displaySelectBox[displaySelectBoxIdx] =
      !copySwitches[switchIdx].displaySelectBox[displaySelectBoxIdx];
    setSwitches(copySwitches);
  };

  // handles all the drop down select boxes such as switch size, heating system and SSR Rating
  const handleSelectBoxes = (switchIdx, selection, ObjKey, SelectedUOS) => {
    let copySwitches = [...switches];

    if (ObjKey === 'UOS') {
      copySwitches = reassignSwitchUOS(switches, switchIdx, selection);
    } else if (ObjKey === 'selectedSSR') {
      const allSelectedSSR = selection.map((ssr) => {
        return {
          [ssr]: {
            thermoCouple: 'tc-01',
            isSettingOpen: false,
            specs: [],
          },
        };
      });

      let obj = Object.assign({}, saveSSRSelectedTemporary, {
        [SelectedUOS]: allSelectedSSR,
      });
      setSaveSSRSelectedTemporary(obj);
      copySwitches[switchIdx][ObjKey] = allSelectedSSR;
    } else if (
      ObjKey === 'heatingSys' &&
      (selection === 'ess' || selection === 'tes')
    ) {
      copySwitches[switchIdx][ObjKey] = selection;
      copySwitches[switchIdx].gasType = '';
      if (selection === 'ess') {
        let newObj = Object.assign({}, saveHeatingSysTemporary, {
          [SelectedUOS]: ['ess'],
        });
        setSaveHeatingSysTemporary(newObj);
      } else {
        let newObj = Object.assign({}, saveHeatingSysTemporary, {
          [SelectedUOS]: ['tgs', 'tes'],
        });
        setSaveHeatingSysTemporary(newObj);
      }
    } else if (
      ObjKey === 'heatingSys' &&
      (selection === 'tgs' || selection === 'tgs/tes')
    ) {
      copySwitches[switchIdx] = applyHeatingSystemSSRRules(
        { ...copySwitches[switchIdx], [ObjKey]: selection },
        selection,
      );
      switches.forEach((switchEl, switchElIdx) => {
        if (
          switchEl.UOS === copySwitches[switchIdx].UOS &&
          !copySwitches[switchIdx].gasType
        ) {
          copySwitches[switchElIdx].gasType = saveGasTypeTemporary[SelectedUOS];
        }
      });

      let newObj = Object.assign({}, saveHeatingSysTemporary, {
        [SelectedUOS]: ['tgs', 'tes'],
      });
      setSaveHeatingSysTemporary(newObj);
    } else if (ObjKey === 'ssrRating') {
      let newObj = Object.assign({}, saveSSRRatingTemporary, {
        [SelectedUOS]: [selection],
      });
      setSaveSSRRatingTemporary(newObj);
      copySwitches[switchIdx][ObjKey] = selection;
    } else if (ObjKey === 'gasType') {
      copySwitches.forEach((switchEl) => {
        if (
          switchEl.UOS === SelectedUOS &&
          switchEl.heatingSys !== 'ess' &&
          switchEl.heatingSys !== 'tes'
        ) {
          switchEl.gasType = selection;
        }
      });
    } else {
      copySwitches[switchIdx][ObjKey] = selection;
    }

    setSwitches(copySwitches);
  };

  // handles all the input boxes of switch name, system i.d., gas type and application.
  const handleInputBoxes = (switchIdx, switchKeys, value, trackUOS) => {
    const copySwitches = [...switches];
    copySwitches[switchIdx][switchKeys] = value;
    setSwitches(copySwitches);
    if (trackUOS) {
      let newObj = Object.assign({}, saveGasTypeTemporary, {
        [trackUOS]: value,
      });
      setSaveGasTypeTemporary(newObj);
    }
  };

  // handle deletion of switch onClick of 'X' button
  const handleDeleteSwitch = (switchIdx) => {
    const {
      UOS,
      type,
      deviceMac,
      heatingSys,
      zoneId,
      latitude,
      longitude,
    } = switches[switchIdx];
    setUOSDelete((prev) => [
      ...prev,
      {
        UOS,
        type: resolvePanelType(type, heatingSys),
        device_mac: deviceMac,
        zone_id: zoneId,
        latitude,
        longitude,
      },
    ]);
    const copySwitches = [...switches];
    copySwitches.splice(switchIdx, 1);
    setNumSwitches(copySwitches.length);
    setSwitches(copySwitches);
    setDeleteSwitch([false]);
    return;
  };

  const handleDeleteSwitchMessageBox = (propArr) => {
    if (propArr[0]) {
      handleDeleteSwitch(propArr[1]);
    } else setDeleteSwitch([false]);
  };

  const [openConfirmMessageBox, setOpenConfirmMessageBox] = useState(false);
  // handle confirm button message box
  const confirmButtonMessageBoxHandler = () => {
    // readForm already guards the ref. In edit mode before a location is
    // picked the input carrying it is not rendered, so .current is null and
    // reading .value directly threw. collectConfirmProblems destructures what
    // it needs, so the extra fields readForm returns are ignored.
    const form = readForm();
    const problems = collectConfirmProblems(form);

    if (problems.length === 0) {
      // Input box has 2 dashes
      // sort switches by group of UOS #
      const sortedUOSArr = [];
      const UOSNameArr = [];
      const expandHeatLoadArr = [];

      new Array(+numOfUOS).fill(0).forEach((emptyArr, idx) => {
        const numUOS = idx + 1;
        const numStr = formatUOSNumber(numUOS);
        const filteredResult = switches.filter((el) => el.UOS === numStr);

        sortedUOSArr.push(filteredResult);
        expandHeatLoadArr.push(false);

        //  grouping all the switches specs info to create the UOS name
        const displayEl = filteredResult.reduce(
          (
            acc,
            { switchName, switchSize, application, heatingSys, gasType },
          ) => {
            if (heatingSys === 'tgs/tes') {
              const elSplit = heatingSys.split('/');

              return (
                `${acc}` +
                // `/${switchName} ${switchSize} ${application} ${elSplit[0]}:${gasType}-${elSplit[1]}` remove heatingSys from naming
                `/ ${switchName} ${switchSize} ${application}:${gasType}`
              );
            } else if (heatingSys === 'tgs') {
              return (
                `${acc}` +
                `/ ${switchName} ${switchSize} ${application}:${gasType}`
              );
            } else {
              return `${acc}` + `/ ${switchName} ${switchSize} ${application}`;
            }
          },
          [],
        );

        UOSNameArr.push(displayEl);
      });

      setIsConfirmed(true);
      setUOSName(UOSNameArr);
      setSwitches(sortedUOSArr);
      setExpandHeatLoad(expandHeatLoadArr);
      setIsGroupedSwitchesSaved(expandHeatLoadArr);
      setTemporaryGreenCircle('confirm');
      setShortCutOfLocationName(form.locationName.split('-')[2] ?? '');
      setOpenConfirmMessageBox(false);
    } else {
      setOpenConfirmMessageBox(false);
      setConfirmProblems(problems);
      setIncorrectLocationNameFormat(true);
    }
  };

  const handleCancel = () => {
    if (isSysIdFormDirty(formSnapshotRef.current, readForm())) {
      setOpenMessageBox(true);
      return;
    }
    exitToChooser();
  };

  const exitToChooser = () => {
    formSnapshotRef.current = null;
    setOpenMessageBox(false);
    setSysIdMode(null);
    setIsConfirmed(false);
    setSelectedLocation(null);
    setCivicAddress('');
    setSpecificLocation('');
    setNumOfUOS('');
    setNumOfSSR('');
    setNumSwitches('');
    setSwitches([]);
    resetConfirmState();
  };

  // Index maps to buttonTitles: 0=cancel, 1=confirm, 2=save.
  const handleButtons = (index) => {
    if (index === 0) {
      handleCancel();
      return;
    }

    if (index === 1) {
      setOpenConfirmMessageBox(true);
      return;
    }

    if (index !== 2) return;

    // An empty edit is the explicit delete-location workflow. Add mode has no
    // existing location to delete, so its zero-switch state must go through the
    // normal validation error instead of raising a delete request. The decision
    // reads the switch rows rather than the number-of-switches input: a typed 0
    // leaves the rows in place, and routing off the counter sent them to the
    // server inside a payload labelled as a delete. A count of 0 with rows
    // still present now falls through to the validation below, which already
    // reports 'number of switches is 0'.
    // This Save stages the form for the section-level Apply action; it does not
    // persist anything itself. Keep the entry snapshot unchanged so Cancel
    // continues to detect unsaved work until Apply actually succeeds.
    if (shouldDeleteLocation(switches, sysIdMode)) {
      handleConfirmOrSave(SYS_ID_SECTION, 'deleteLocation');
      setTemporaryGreenCircle('save');
      return;
    }

    if (!isConfirmed) {
      const problems = collectConfirmProblems(readForm());
      problems.push('confirm system identification before saving');
      setConfirmProblems(problems);
      setIncorrectLocationNameFormat(true);
      return;
    }

    handleConfirmOrSave(SYS_ID_SECTION);
    if (isGroupedSwitchesSaved.length > 0) {
      handleConfirmOrSave(SYS_ID_SECTION, 'allTheUOSSaved');
      setIsGroupedSwitchesSaved(isGroupedSwitchesSaved.map(() => true));
      setTemporaryGreenCircle('save');
    }
  };

  useEffect(() => {
    const handleTemporaryGreenCircle = setTimeout(() => {
      setTemporaryGreenCircle(false);
    }, 500);

    return () => clearTimeout(handleTemporaryGreenCircle);
  }, [temporaryGreenCircle]);

  const handleExpandHeatLoadConfiguration = (groupSwitchesIdx) => {
    const copyExpandHeatLoad = [...expandHeatLoad];
    copyExpandHeatLoad[groupSwitchesIdx] =
      !copyExpandHeatLoad[groupSwitchesIdx];
    setExpandHeatLoad(copyExpandHeatLoad);
  };

  const handleSaveButtonOfEachUOS = (groupedIdx) => {
    const copyIsGroupedSwitchesSaved = [...isGroupedSwitchesSaved];
    copyIsGroupedSwitchesSaved[groupedIdx] = true;
    setIsGroupedSwitchesSaved(copyIsGroupedSwitchesSaved);
    // This button commits the group's heat-load editor to local form state.
    // Only the top-level Save may arm API persistence; persisting a subset of
    // UOS groups alongside whole-location counts creates an invalid topology.
    handleExpandHeatLoadConfiguration(groupedIdx);
  };

  const getAvailableSSRs = (currentSwitchIdx, currentUOS) => {
    const usedSSRs = new Set();
    switches.forEach((switch_, idx) => {
      if (idx !== currentSwitchIdx && switch_.UOS === currentUOS) {
        switch_.selectedSSR.forEach((ssr) => {
          usedSSRs.add(Object.keys(ssr)[0]);
        });
      }
    });

    return listOfSelectedSSR.filter((ssr) => !usedSSRs.has(ssr));
  };

  const getAvailableUOSPanels = (currentSwitchIdx) =>
    availableUOSPanels(listOfUOS, switches, currentSwitchIdx);

  return (
    <BaseLayer>
      <Wrapper>
        <TitleWrapper>
          <Title>
            system identification{sysIdMode ? ` — ${sysIdMode}` : ''}
          </Title>
        </TitleWrapper>
        {/* location, address, switches and close button */}
        <ContentBaseLayer>
          {sysIdMode === null ? (
            <SystemIdentificationChooser
              locations={locations}
              onAdd={handleEnterAdd}
              onEdit={handleEnterEdit}
            />
          ) : (
            <ContentWrapper>
              <FlexSpaceEvenlyWrapper>
                <SystemSelectSpacer />
                {/* location name */}
                <IndivFlex location={true}>
                  <SubTitle>location name</SubTitle>
                  {/* The input is unmounted while the chooser shows, so it mounts
                      fresh on entering a mode: add starts empty, edit is prefilled
                      from the chosen location. Uncontrolled, so it stays editable
                      and a location can still be renamed. Because defaultValue is
                      only read at mount, changing selectedLocation while already
                      in a mode will not update the field. */}
                  {/* The format is enforced by collectConfirmProblems, which
                      rejects a name without two dashes. Stated up front here so
                      the rule is discoverable before confirm rather than after.
                      Shown in edit too — renaming is allowed and validated the
                      same way. */}
                  <ContentInput
                    type='text'
                    location={true}
                    readOnly={isConfirmed}
                    placeholder='COMPANY-Location Name-SHORTCUT'
                    title='format: COMPANY-Location Name-SHORTCUT (two dashes required)'
                    defaultValue={selectedLocation?.location ?? ''}
                    ref={locationNameRef}
                  ></ContentInput>
                </IndivFlex>
                {/* specific location name */}
                <IndivFlex specificLocation={true}>
                  <SubTitle>specific location name</SubTitle>
                  <ContentInput
                    required
                    min='3'
                    type='text'
                    specificLocation={true}
                    name='specific location'
                    value={specificLocation}
                    readOnly={isConfirmed}
                    onChange={(e) => setSpecificLocation(e.target.value)}
                    // ref={specificLocationRef}
                  ></ContentInput>
                </IndivFlex>
              </FlexSpaceEvenlyWrapper>
              <FlexStartWrapper>
                <Flex>
                  {/* number of UOS panels */}
                  <IndivFlex isUOS={true}>
                    <SubTitle>number of uos panels</SubTitle>
                    <ContentInput
                      type='number'
                      min='1'
                      step='1'
                      isUOS={true}
                      readOnly={isConfirmed}
                      value={numOfUOS}
                      onChange={(e) => setNumOfUOS(Number(e.target.value))}
                    ></ContentInput>
                  </IndivFlex>
                  {/* number of ssr */}
                  <IndivFlex isSSR={true}>
                    <SubTitle>ssr qty.</SubTitle>
                    <ContentInput
                      type='number'
                      min='0'
                      step='1'
                      isSSR={true}
                      readOnly={isConfirmed}
                      value={numOfSSR}
                      onChange={(e) => setNumOfSSR(Number(e.target.value))}
                    ></ContentInput>
                  </IndivFlex>
                  {/* number of switches */}
                  <IndivFlex switches={true}>
                    <SubTitle switches={true}>
                      {selectSystem ? 'number of zones' : 'number of switches'}
                    </SubTitle>
                    <ContentInput
                      type='number'
                      min='1'
                      step='1'
                      switches={true}
                      readOnly={isConfirmed}
                      value={numSwitches}
                      onChange={(e) => setNumSwitches(+e.target.value)}
                    ></ContentInput>
                  </IndivFlex>
                </Flex>
                <Flex isCivilAddress={true}>
                  {/* civic address */}
                  <IndivFlex address={true}>
                    <SubTitle>civic address</SubTitle>
                    <ContentInput
                      type='text'
                      address={true}
                      readOnly={isConfirmed}
                      value={civicAddress}
                      onChange={(e) => setCivicAddress(e.target.value)}
                      ref={civicAddressRef}
                    ></ContentInput>
                    <AutoCompletePlaces
                      civicAddress={civicAddress}
                      setCivicAddress={setCivicAddress}
                      inputRef={civicAddressRef}
                    />
                  </IndivFlex>
                </Flex>
              </FlexStartWrapper>
              {/* switches info titles */}
              <FlexSwitches>
                <SwitchTitlesWrapper>
                  <SubTitle isUOS={true}>uos</SubTitle>
                  {selectSystem ? (
                    <>
                      <SubTitle isZoneName={true}>zone name</SubTitle>
                      <SubTitle isSegment={true}>segment</SubTitle>
                    </>
                  ) : (
                    <>
                      <SubTitle isSwitchName={true}>switch name</SubTitle>
                      <SubTitle isSwitchSize={true}>switch size</SubTitle>
                    </>
                  )}
                  <SubTitle isSysId={true}> system i.d.</SubTitle>
                  <SubTitle isHeatingSys={true}>heating system</SubTitle>
                  <SubTitle isGasType={true}>gas type</SubTitle>
                  <SubTitle isSelectedSSR={true}>ssr qty.</SubTitle>
                  <SubTitle isSsrRating={true}>ssr rating</SubTitle>
                  {/* <SubTitle isApp={true}>application</SubTitle> */}
                </SwitchTitlesWrapper>
                {/* below check if the confirm button is clicked   */}
                {/* confirm button is set to true */}

                {isConfirmed ? (
                  <>
                    <ConfirmedUOSInfo
                      selectSystem={selectSystem}
                      data={switches}
                      switchSizeOptions={switchSizeOptions}
                      isCreateEditOrSave={isCreateEditOrSave}
                      saveHeatingSysTemporary={saveHeatingSysTemporary}
                      heatingSysOptions={heatingSysOptions}
                      listOfSelectedSSR={listOfSelectedSSR}
                      saveSSRRatingTemporary={saveSSRRatingTemporary}
                      ssrRatingOptions={ssrRatingOptions}
                      expandHeatLoad={expandHeatLoad}
                      setSwitches={setSwitches}
                      switches={switches}
                      shortCutOfLocationName={shortCutOfLocationName}
                      UOSName={UOSName}
                      handleSaveButtonOfEachUOS={handleSaveButtonOfEachUOS}
                      handleExpandHeatLoadConfiguration={
                        handleExpandHeatLoadConfiguration
                      }
                    />
                  </>
                ) : (
                  <>
                    {initialStart && (
                      <InitialUOSInfo
                        selectSystem={selectSystem}
                        data={switches}
                        switchSizeOptions={switchSizeOptions}
                        isCreateEditOrSave={isCreateEditOrSave}
                        saveHeatingSysTemporary={saveHeatingSysTemporary}
                        saveSSRSelectedTemporary={saveSSRSelectedTemporary}
                        heatingSysOptions={heatingSysOptions}
                        listOfSelectedSSR={(switchIdx, UOS) =>
                          getAvailableSSRs(switchIdx, UOS)
                        }
                        handleOpenSelectBoxes={handleOpenSelectBoxes}
                        handleSelectBoxes={handleSelectBoxes}
                        listOfUOS={getAvailableUOSPanels}
                        ssrRatingOptions={ssrRatingOptions}
                        handleInputBoxes={handleInputBoxes}
                        gasTypeOptions={gasTypeOptions}
                        setDeleteSwitch={setDeleteSwitch}
                        deleteSwitch={deleteSwitch}
                      />
                    )}
                  </>
                )}
              </FlexSwitches>
            </ContentWrapper>
          )}
        </ContentBaseLayer>
        {/* cancel, confirm and save buttons. Hidden in the chooser state:
            there is no form to act on, and confirm would dereference the
            unmounted location-name input. */}
        {sysIdMode !== null && (
          <GroupButtonsFlexEnd>
            <GroupButtonsBaseLayer1>
              <GroupButtonsBaseLayer2>
                <GroupButtonsWrapper>
                  {buttonTitles.map((title, index) => {
                    return (
                      <Button
                        isSelectedColor={temporaryGreenCircle === title}
                        disabled={title === 'confirm' && isConfirmed}
                        key={index}
                        onClick={() => {
                          handleButtons(index);
                        }}
                      >
                        <ButtonHole>
                          <ButtonTop>
                            <ButtonTitle>{title}</ButtonTitle>
                          </ButtonTop>
                        </ButtonHole>
                      </Button>
                    );
                  })}
                </GroupButtonsWrapper>
              </GroupButtonsBaseLayer2>
            </GroupButtonsBaseLayer1>
          </GroupButtonsFlexEnd>
        )}
        {/* message box popup when click on "X" button */}
        {deleteSwitch[0] && (
          <MessageBoxWrapper>
            <DeleteSwitchMessageBox
              title={'settings/system identification'}
              subtitle={'administration settings'}
              theme={'system identification'}
              messages={[
                `you are about to delete switch ${
                  switches[deleteSwitch[1]]?.switchName
                } & change the configuration`,
              ]}
              onClose={() => handleDeleteSwitchMessageBox([])}
              onDelete={() => handleDeleteSwitchMessageBox(deleteSwitch)}
            />
          </MessageBoxWrapper>
        )}
        {/* message box for missing information of input box location name. eg: the format needs to have 2 dashes  */}
        {incorrectLocationNameFormat && (
          <MessageBoxWrapper isLocationName={true}>
            <ErrorMessageBox
              message={{
                content: confirmProblems.join('\n'),
                title: 'settings',
                subtitle: 'administration settings',
                theme: 'missing information',
              }}
              handleClose={() => {
                setIncorrectLocationNameFormat(false);
                setConfirmProblems([]);
              }}
            />
          </MessageBoxWrapper>
        )}
        {openConfirmMessageBox && (
          <MessageBoxWrapper isLocationName={true}>
            <SettingConfirmCancelMessageBox
              onCancel={() => setOpenConfirmMessageBox(false)}
              message={{
                title: 'settings',
                subtitle: 'administration settings',
                theme: 'confirmation',
                message: [
                  'are you sure you want to confirm? once it is confirmed, the data above can no longer be modified.',
                ],
              }}
              onConfirm={() => confirmButtonMessageBoxHandler()}
            />
          </MessageBoxWrapper>
        )}
        {openMessageBox && (
          <SettingClearOkMessage
            onClose={() => setOpenMessageBox(false)}
            title={'unsaved changes'}
            message={
              'this system has changes you have not saved. click "clear" to discard them and go back, or "ok" to keep editing.'
            }
            onClear={exitToChooser}
          />
        )}
      </Wrapper>
    </BaseLayer>
  );
};

export default SystemIdentification;

//  after Confirmed use the code below

// {switches?.map((groupedSwitches, groupedIdx) => {
//   return (
//     <div key={groupedSwitches[0]?.sysId + groupedIdx}>
//       <SortedUOSGroupWrapper>
//         <FlexRow isFlexStart={true}>
//           <UOSDisplay>
//             {groupedIdx < 9
//               ? '0' + (Number(groupedIdx) + 1)
//               : toString(Number(groupedIdx) + 1)}
//           </UOSDisplay>
//           <FlexRow isFlexColumn={true}>
//             {groupedSwitches?.map(
//               (
//                 {
//                   UOS,
//                   switchName,
//                   switchSize,
//                   sysId,
//                   heatingSys,
//                   gasType,
//                   selectedSSR,
//                   ssrRating,
//                   application,
//                   displaySelectBox,
//                 },
//                 idx
//               ) => {
//                 return (
//                   <FlexRow key={switchName}>
//                     <SwitchesBaseLayer isGrouped={true}>
//                       <SwitchesWrapper isGrouped={true}>
//                         {/* switch name */}
//                         <SwitchesInputBox
//                           isGrouped={true}
//                           type='text'
//                           esInputBox
//                           switchName={true}
//                           value={switchName}
//                           readOnly={true}
//                           // onChange={(e) =>
//                           //   handleInputBoxes(
//                           //     idx,
//                           //     'switchName',
//                           //     e.target.value,
//                           //     groupedIdx
//                           //   )
//                           // }
//                         ></SwitchesInputBox>

//                         {/* switch size */}
//                         <SelectBox
//                           isSwitchSize={true}
//                           displaySelectBox={
//                             displaySelectBox[1]
//                           }
//                           switchIdx={idx}
//                           // handleOpenSelectBoxes={() =>
//                           //   handleOpenSelectBoxes(idx, 1)
//                           // }
//                           selectBoxFor={'switchSize'}
//                           // handleSelectBoxes={
//                           //   handleSelectBoxes
//                           // }
//                           selected={switchSize}
//                           content={switchSizeOptions}
//                           isCreateEditOrSave={
//                             isCreateEditOrSave === 0 ||
//                             isCreateEditOrSave === 1
//                           }
//                           buttonTitle={'add size'}
//                         />

//                         {/* system i.d. */}
//                         <SwitchesInputBox
//                           type='number'
//                           systemId={true}
//                           value={sysId ? sysId : 'undefined'}
//                           readOnly={true}
//                           // onChange={(e) =>
//                           //   handleInputBoxes(
//                           //     idx,
//                           //     'sysId',
//                           //     e.target.value
//                           //   )
//                           // }
//                         ></SwitchesInputBox>

//                         {/* heating system */}
//                         <SelectBox
//                           isHeatingSys={true}
//                           displaySelectBox={
//                             displaySelectBox[2]
//                           }
//                           switchIdx={idx}
//                           // handleOpenSelectBoxes={() =>
//                           //   handleOpenSelectBoxes(idx, 2)
//                           // }
//                           // handleSelectBoxes={
//                           //   handleSelectBoxes
//                           // }
//                           selected={heatingSys}
//                           content={
//                             saveHeatingSysTemporary[UOS]
//                               ? saveHeatingSysTemporary[UOS]
//                               : heatingSysOptions
//                           }
//                           selectBoxFor={'heatingSys'}
//                           clearButtonTitle={'clear'}
//                           isMultipleSelections={true}
//                         />

//                         {/* Gas Type */}
//                         {heatingSys === 'ess' ||
//                         heatingSys === 'tes' ? (
//                           <FakeContentInput
//                             isGasType={true}
//                           ></FakeContentInput>
//                         ) : (
//                           <SwitchesInputBox
//                             type='text'
//                             gasType={true}
//                             value={gasType}
//                             readOnly={true}
//                             // onChange={(e) =>
//                             //   handleInputBoxes(
//                             //     idx,
//                             //     'gasType',
//                             //     e.target.value
//                             //   )
//                             // }
//                           ></SwitchesInputBox>
//                         )}

//                         {/* SSR QTY */}
//                         <SelectBox
//                           isSSRUnits={true}
//                           displaySelectBox={
//                             displaySelectBox[3]
//                           }
//                           switchIdx={idx}
//                           // handleOpenSelectBoxes={() =>
//                           //   handleOpenSelectBoxes(idx, 3)
//                           // }
//                           // handleSelectBoxes={
//                           //   handleSelectBoxes
//                           // }
//                           selected={
//                             selectedSSR.length > 0 &&
//                             selectedSSR.length < 10
//                               ? selectedSSR.length > 0
//                                 ? '0' + selectedSSR.length
//                                 : selectedSSR.length
//                               : ''
//                           }
//                           allSelections={selectedSSR}
//                           content={listOfSelectedSSR}
//                           selectBoxFor={'selectedSSR'}
//                           clearButtonTitle={'clear'}
//                           isMultipleSelections={true}
//                         />

//                         {/* SSR rating  */}
//                         <SelectBox
//                           isSSRRating={true}
//                           displaySelectBox={
//                             displaySelectBox[4]
//                           }
//                           switchIdx={idx}
//                           // handleOpenSelectBoxes={() =>
//                           //   handleOpenSelectBoxes(idx, 4)
//                           // }
//                           // handleSelectBoxes={
//                           //   handleSelectBoxes
//                           // }
//                           selected={ssrRating}
//                           content={
//                             saveSSRRatingTemporary[UOS]
//                               ? saveSSRRatingTemporary[UOS]
//                               : ssrRatingOptions
//                           }
//                           selectBoxFor={'ssrRating'}
//                           isCreateEditOrSave={
//                             isCreateEditOrSave === 0 ||
//                             isCreateEditOrSave === 1
//                           }
//                           buttonTitle={'add ssr rating'}
//                         />

//                         {/* application */}
//                         <SwitchesInputBox
//                           type='text'
//                           application={true}
//                           value={application}
//                           readOnly={true}
//                           // onChange={(e) =>
//                           //   handleInputBoxes(
//                           //     idx,
//                           //     'application',
//                           //     e.target.value
//                           //   )
//                           // }
//                         ></SwitchesInputBox>
//                         {/* ********  */}
//                       </SwitchesWrapper>
//                     </SwitchesBaseLayer>

//                     {/* X button to delete switch */}
//                     {(isCreateEditOrSave === 1 ||
//                       isCreateEditOrSave === 0) && (
//                       <XButtonWrapper
//                       // onClick={() =>
//                       //   handleDeleteSwitch(idx)
//                       // }
//                       >
//                         <XButton>
//                           <XButtonTop>
//                             <Img
//                               src={
//                                 './images/settings-admin-sysIdentification-whiteX.svg'
//                               }
//                               margin={'x'}
//                             />
//                           </XButtonTop>
//                         </XButton>
//                       </XButtonWrapper>
//                     )}
//                   </FlexRow>
//                 );
//               }
//             )}
//           </FlexRow>
//         </FlexRow>

//         {/* expanded Heat Load Configuration */}
//         {expandHeatLoad[groupedIdx] && (
//           <>
//             <Title isHeatLoad={true}>
//               heat load configuration
//             </Title>
//             <SSRElementsWrapper>
//               <SSRElements
//                 SSRList={listOfSelectedSSR}
//                 groupedSwitches={groupedSwitches}
//                 groupedIdx={groupedIdx}
//                 setSwitches={setSwitches}
//                 switches={switches}
//               />
//             </SSRElementsWrapper>
//           </>
//         )}

//         {/* !!! display UOS name and Heat load configuration button!!! */}
//         <FlexRow
//           isLastRow={
//             expandHeatLoad[groupedIdx] ? 'sorted' : true
//           }
//         >
//           <DisplayUOSName>
//             <UOSNameP>
//               {shortCutOfLocationName} -{' '}
//               {UOSName[groupedIdx].slice(1)}
//             </UOSNameP>
//           </DisplayUOSName>
//           {expandHeatLoad[groupedIdx] ? (
//             <HeatLoadButtonWrapper isApply={true}>
//               <HeatLoadButton
//                 isApply={true}
//                 onClick={() => {
//                   handleSaveButtonOfEachUOS(groupedIdx);
//                 }}
//               >
//                 <HeatLoadButtonIndent isApply={true}>
//                   <HeatLoadButtonTop isApply={true}>
//                     <HeatLoadSpan>save</HeatLoadSpan>
//                   </HeatLoadButtonTop>
//                 </HeatLoadButtonIndent>
//               </HeatLoadButton>
//             </HeatLoadButtonWrapper>
//           ) : (
//             <HeatLoadButtonWrapper>
//               <HeatLoadButton
//                 onClick={() =>
//                   handleExpandHeatLoadConfiguration(
//                     groupedIdx
//                   )
//                 }
//               >
//                 <HeatLoadButtonIndent>
//                   <HeatLoadButtonTop>
//                     <HeatLoadSpan>
//                       heat load configuration
//                     </HeatLoadSpan>
//                   </HeatLoadButtonTop>
//                 </HeatLoadButtonIndent>
//               </HeatLoadButton>
//             </HeatLoadButtonWrapper>
//           )}
//         </FlexRow>
//       </SortedUOSGroupWrapper>
//     </div>
//   );
// })}

///////////////////////////////////////////////////////

// Initial state use the code below

// switches?.map(
//   (
//     {
//       UOS,
//       switchName,
//       switchSize,
//       sysId,
//       heatingSys,
//       gasType,
//       selectedSSR,
//       ssrRating,
//       application,
//       displaySelectBox,
//     },
//     idx
//   ) => {
//     return (
//       <FlexRow key={idx}>
//         <SwitchesBaseLayer>
//           <SwitchesWrapper>
//             {/* UOS */}
//             <SelectBox
//               isUOS={true}
//               displaySelectBox={displaySelectBox?.[0]}
//               switchIdx={idx}
//               handleOpenSelectBoxes={() =>
//                 handleOpenSelectBoxes(idx, 0)
//               }
//               handleSelectBoxes={handleSelectBoxes}
//               selected={UOS}
//               content={listOfUOS}
//               selectBoxFor={'UOS'}
//             />

//             {/* switch name */}
//             <SwitchesInputBox
//               type='text'
//               esInputBox
//               switchName={true}
//               value={switchName}
//               onChange={(e) =>
//                 handleInputBoxes(
//                   idx,
//                   'switchName',
//                   e.target.value
//                 )
//               }
//             ></SwitchesInputBox>
//             {/* switch size */}
//             <SelectBox
//               isSwitchSize={true}
//               displaySelectBox={displaySelectBox[1]}
//               switchIdx={idx}
//               handleOpenSelectBoxes={() =>
//                 handleOpenSelectBoxes(idx, 1)
//               }
//               selectBoxFor={'switchSize'}
//               handleSelectBoxes={handleSelectBoxes}
//               selected={switchSize}
//               content={switchSizeOptions}
//               isCreateEditOrSave={
//                 isCreateEditOrSave === 0 ||
//                 isCreateEditOrSave === 1
//               }
//               buttonTitle={'add size'}
//             />

//             {/* system i.d. */}
//             <SwitchesInputBox
//               type='number'
//               readOnly={true}
//               systemId={true}
//               value={sysId ? sysId : 'undefined'}
//               // onChange={(e) =>
//               //   handleInputBoxes(
//               //     idx,
//               //     'sysId',
//               //     e.target.value
//               //   )
//               // }
//             ></SwitchesInputBox>

//             {/* heating system */}
//             <SelectBox
//               isHeatingSys={true}
//               displaySelectBox={displaySelectBox[2]}
//               switchIdx={idx}
//               handleOpenSelectBoxes={() =>
//                 handleOpenSelectBoxes(idx, 2)
//               }
//               handleSelectBoxes={handleSelectBoxes}
//               selected={heatingSys}
//               content={
//                 saveHeatingSysTemporary[UOS]
//                   ? saveHeatingSysTemporary[UOS]
//                   : heatingSysOptions
//               }
//               selectBoxFor={'heatingSys'}
//               clearButtonTitle={'clear'}
//               isMultipleSelections={true}
//               selectedUOS={UOS}
//             />

//             {/* Gas Type */}
//             {heatingSys === 'ess' ||
//             heatingSys === 'tes' ? (
//               <FakeContentInput
//                 isGasType={true}
//               ></FakeContentInput>
//             ) : (
//               <SelectBox
//                 isGasType={true}
//                 displaySelectBox={displaySelectBox[5]}
//                 switchIdx={idx}
//                 handleOpenSelectBoxes={() =>
//                   handleOpenSelectBoxes(idx, 5)
//                 }
//                 handleSelectBoxes={handleSelectBoxes}
//                 selected={gasType}
//                 content={gasTypeOptions}
//                 selectBoxFor={'gasType'}
//                 isCreateEditOrSave={
//                   isCreateEditOrSave === 0 ||
//                   isCreateEditOrSave === 1
//                 }
//                 buttonTitle={'add gas type'}
//                 selectedUOS={UOS}
//               />
//             )}

//             {/* SSR QTY. */}
//             {heatingSys === 'tgs' ? (
//               <FakeSelectBox isSSRUnits={true} />
//             ) : (
//               <SelectBox
//                 isSSRUnits={true}
//                 displaySelectBox={displaySelectBox[3]}
//                 switchIdx={idx}
//                 handleOpenSelectBoxes={() =>
//                   handleOpenSelectBoxes(idx, 3)
//                 }
//                 handleSelectBoxes={handleSelectBoxes}
//                 selected={
//                   selectedSSR &&
//                   selectedSSR.length > 0 &&
//                   selectedSSR.length < 10
//                     ? selectedSSR.length > 0
//                       ? '0' + selectedSSR.length
//                       : selectedSSR.length
//                     : ''
//                 }
//                 allSelections={selectedSSR}
//                 content={listOfSelectedSSR}
//                 selectBoxFor={'selectedSSR'}
//                 clearButtonTitle={'clear'}
//                 isMultipleSelections={true}
//               />
//             )}

//             {/* SSR rating  */}
//             {heatingSys === 'tgs' ? (
//               <FakeSelectBox isSSRRating={true} />
//             ) : (
//               <SelectBox
//                 isSSRRating={true}
//                 displaySelectBox={displaySelectBox[4]}
//                 switchIdx={idx}
//                 handleOpenSelectBoxes={() =>
//                   handleOpenSelectBoxes(idx, 4)
//                 }
//                 handleSelectBoxes={handleSelectBoxes}
//                 selected={ssrRating}
//                 content={
//                   // saveSSRRatingTemporary[UOS]
//                   //   ? saveSSRRatingTemporary[UOS]
//                   //   :
//                   ssrRatingOptions
//                 }
//                 selectBoxFor={'ssrRating'}
//                 isCreateEditOrSave={
//                   isCreateEditOrSave === 0 ||
//                   isCreateEditOrSave === 1
//                 }
//                 buttonTitle={'add ssr rating'}
//                 selectedUOS={UOS}
//               />
//             )}

//             {/* application */}
//             <SwitchesInputBox
//               type='text'
//               application={true}
//               value={application}
//               onChange={(e) =>
//                 handleInputBoxes(
//                   idx,
//                   'application',
//                   e.target.value
//                 )
//               }
//             ></SwitchesInputBox>

//             {/* X Button  */}
//           </SwitchesWrapper>
//         </SwitchesBaseLayer>
//         {(isCreateEditOrSave === 1 ||
//           isCreateEditOrSave === 0) && (
//           <XButtonWrapper
//             onClick={() => setDeleteSwitch([true, idx])}
//             isGreenBorder={deleteSwitch[1] === idx}
//           >
//             <XButton>
//               <XButtonTop>
//                 <Img
//                   src={
//                     './images/settings-admin-sysIdentification-whiteX.svg'
//                   }
//                   margin={'x'}
//                 />
//               </XButtonTop>
//             </XButton>
//           </XButtonWrapper>
//         )}
//       </FlexRow>
//     );
//   }
// )

////////////////////////////////////////////////////////////////

// (
//   <SwitchesInputBox
//     type='text'
//     gasType={true}
//     value={gasType}
//     onChange={(e) =>
//       handleInputBoxes(
//         idx,
//         'gasType',
//         e.target.value,
//         UOS
//       )
//     }
//   ></SwitchesInputBox>
// )

const BaseLayer = styled.div`
  width: 843px;
  height: auto;
  padding-top: 1px;
  padding-bottom: 1px;

  ${layerB}

  border-radius: 9px;
  opacity: 1;
  ${flexBoxCenter}
`;
const Wrapper = styled.div`
  width: 841px;
  height: auto;

  ${layerA180Deg}

  border-radius: 8px;
  opacity: 1;

  ${justifyContentSpaceEvenly}
  flex-direction: column;
  position: relative;
`;

const TitleWrapper = styled.div`
  width: 829px;
  height: 32px;
  margin-top: 6px;

  ${layerA}

  border-radius: 16px;
  opacity: 1;
  ${flexBoxCenter}
`;

const Title = styled.div`
  color: #ffffff;

  ${({ isHeatLoad }) =>
    isHeatLoad
      ? css`
          margin-top: 8px;
          font-size: 10px;

          display: flex;
          flex-direction: row;

          &:before {
            content: '';
            width: 314px;
            border-bottom: 1px solid #ffffff;
            margin: auto;
            margin-right: 4px;
          }
          &:after {
            content: '';
            width: 314px;
            border-bottom: 1px solid #ffffff;
            margin: auto;
            margin-left: 4px;
          }
        `
      : css`
          font-size: 12px;
          letter-spacing: 1.2px;
        `}
`;

const FlexRow = styled.div`
  position: relative;

  ${({ isLastRow }) =>
    isLastRow === 'sorted'
      ? css`
          ${justifyContentSpaceAround}
          margin-top: 6px;
          margin-bottom: 2px;
        `
      : isLastRow
        ? css`
            ${justifyContentFlexStart}
            margin-top: 6px;
          `
        : css`
            ${justifyContentSpaceEvenly}
          `}

  ${({ isFlexColumn, isFlexStart }) =>
    isFlexColumn
      ? css`
          flex-direction: column;
        `
      : isFlexStart &&
        css`
          display: flex;
          justify-content: space-around;
          align-items: flex-start;
        `}
`;

const ContentBaseLayer = styled.div`
  width: 829px;
  height: auto;
  padding-top: 1px;
  padding-bottom: 1px;
  margin-top: 8px;

  ${layerA}

  border-radius: 18px;
  opacity: 1;
  ${flexBoxCenter}
`;

const ContentWrapper = styled.div`
  width: 827px;
  height: auto;
  padding-top: 4px;
  padding-bottom: 4px;

  ${layerA180Deg}

  border-radius: 17px;
  opacity: 1;
`;

const FlexSpaceEvenlyWrapper = styled.div`
  height: 24px;
  margin-top: 4px;
  margin-bottom: 8px;
  width: 100%;
  ${justifyContentSpaceEvenly}
`;

// Holds the width the transit-station toggle used to occupy, so the row's
// space-evenly distribution is unchanged while the toggle is disabled.
const SystemSelectSpacer = styled.div`
  width: 112px;
  height: 28px;
`;

const FlexStartWrapper = styled.div`
  /* width: 332px; */
  width: auto;
  height: 24px;
  margin-top: 4px;
  margin-bottom: 8px;
  ${justifyContentFlexStart}
  gap: 96px;
  /* gap: 85px; */
`;

const Flex = styled.div`
  ${({ isCivilAddress }) =>
    isCivilAddress
      ? css`
          width: 402px;
          /* width: 450px; */
        `
      : css`
          width: 352px;
        `}
  ${justifyContentSpaceAround}
`;

const IndivFlex = styled.div`
  ${({ location, address, switches, isSSR, isUOS, specificLocation }) =>
    location || specificLocation
      ? css`
          /* width: 402px; */
          width: 342px;
          ${justifyContentSpaceAround};
          ${({ address }) =>
            address &&
            css`
              ${justifyContentSpaceBetween}
            `}
        `
      : address
        ? css`
            width: 402px;
            ${justifyContentSpaceAround};
          `
        : (switches || isSSR || isUOS) &&
          css`
            max-width: 126px;
            margin-left: 7px;
            ${alignItemsFlexStart};
          `}
`;

const SubTitle = styled.span`
  /* height: 24px; */
  height: fit-content;
  width: auto;
  text-align: left;
  font-size: 10px;
  letter-spacing: 1px;
  color: #ffffff;
  opacity: 1;
  ${({
    isZoneName,
    isSegment,
    isHeatingSys,
    isSwitchName,
    isSwitchSize,
    isSysId,
    isSsrRating,
    isApp,
    isSwitches,
    isUOS,
    isGasType,
    isSelectedSSR,
  }) =>
    isHeatingSys
      ? css`
          width: 56px;
          position: absolute;
          top: 0px;
          left: 438px;
        `
      : isSwitchName
        ? css`
            height: 14px;
            width: auto;
            position: absolute;
            top: 6px;
            left: 80px;
          `
        : isSwitchSize
          ? css`
              height: 22px;
              width: 52px;
              text-align: center;
              position: absolute;
              top: 0px;
              left: 206px;
            `
          : isSysId
            ? css`
                height: 14px;
                width: auto;
                position: absolute;
                top: 6px;
                left: 308px;
              `
            : isSsrRating
              ? css`
                  height: 14px;
                  width: auto;
                  position: absolute;
                  top: 6px;
                  left: 648px;
                `
              : isApp
                ? css`
                    height: 14px;
                    width: auto;
                    position: absolute;
                    top: 6px;
                    left: 710px;
                  `
                : isSwitches
                  ? css`
                      width: auto;
                    `
                  : isUOS
                    ? css`
                        height: 14px;
                        width: 26px;
                        position: absolute;
                        top: 6px;
                        left: 20px;
                      `
                    : isGasType
                      ? css`
                          width: 34px;
                          text-align: center;
                          position: absolute;
                          top: 0px;
                          left: 524px;
                        `
                      : isSelectedSSR
                        ? css`
                            width: 26px;
                            position: absolute;
                            top: 0px;
                            left: 588px;
                          `
                        : isSegment
                          ? css`
                              height: 14px;
                              width: 64px;
                              text-align: center;
                              position: absolute;
                              top: 6px;
                              left: 202px;
                            `
                          : isZoneName &&
                            css`
                              height: 14px;
                              width: auto;
                              position: absolute;
                              top: 6px;
                              left: 86px;
                            `}
`;

const ContentInput = styled.input`
  ${({ location, address, switches, isUOS, isSSR, specificLocation }) =>
    location
      ? css`
          width: 346px;
          height: 24px;
        `
      : address
        ? css`
            width: 260px;
            height: 24px;
          `
        : specificLocation
          ? css`
              /* width: 320px; */
              width: 254px;
              height: 24px;
            `
          : (isUOS || isSSR || switches) &&
            css`
              width: 34px;
              height: 24px;
              border-radius: 12px;
            `}

  font-size:8px;
  letter-spacing: 0.8px;
  text-transform: uppercase;

  ${layerADark}

  /* The input sits on #233a54, where the browser's default placeholder colour
     is all but invisible. */
  &::placeholder {
    color: #a9a9a9;
    opacity: 1;
  }

  border-radius: 12px;
  opacity: 1;
`;

// const LocationNameSelectBoxBaseLayer = styled.div`
//   width: 322px;
//   height: 22px;

//   ${layerADark}

//   border-radius: 13px;
//   opacity: 1;
//   ${flexBoxCenter}
// `;

// const ButtonWrapper = styled.div`
//   width: 77px;
//   height: 24px;

//   ${layerB}

//   border-radius: 15px;
//   opacity: 1;
//   ${flexBoxCenter}
// `;

const GroupButtonsFlexEnd = styled.div`
  width: 99.2%;
  margin-top: 8px;
  margin-bottom: 4px;
  ${justifyContentFlexEnd}
`;

const GroupButtonsBaseLayer1 = styled.div`
  width: 536px;
  height: 35px;

  ${layerA}

  border-radius: 18px;
  opacity: 1;
  ${flexBoxCenter}
`;

const GroupButtonsBaseLayer2 = styled.div`
  width: 534px;
  height: 33px;

  ${layerA180Deg}

  border-radius: 17px;
  opacity: 1;
  ${flexBoxCenter}
`;

const GroupButtonsWrapper = styled.div`
  width: 530px;
  height: 29px;

  ${layerB}

  border-radius: 15px;
  opacity: 1;
  ${justifyContentSpaceBetween}
`;

const Button = styled.button`
  width: 124px;
  height: 27px;

  ${layerA180Deg}

  border-radius: 25px;
  opacity: 1;
  &:last-child {
    margin-right: 1px;
  }
  &:first-child {
    margin-left: 1px;
  }

  ${({ expandButton }) =>
    expandButton &&
    css`
      width: 75px;
      height: 22px;
    `}

  ${({ isSelectedColor }) =>
    isSelectedColor &&
    css`
      border: 1px solid #95ff45;
    `}

  ${flexBoxCenter}
`;

const ButtonHole = styled.div`
  width: 118px;
  height: 21px;

  ${layerB}

  border-radius: 20px;
  opacity: 0.8;

  ${({ expandButton }) =>
    expandButton &&
    css`
      width: 69px;
      height: 16px;
    `}

  ${flexBoxCenter}
`;

const ButtonTop = styled.div`
  width: 116px;
  height: 19px;

  ${layerA180Deg}

  border-radius: 25px;
  opacity: 1;

  ${({ expandButton }) =>
    expandButton &&
    css`
      width: 67px;
      height: 14px;
    `}

  ${flexBoxCenter}
`;

const ButtonTitle = styled.div`
  font-size: 10px;
  letter-spacing: 1px;
  color: #ffffff;
  opacity: 1;
`;

const FlexSwitches = styled.div`
  width: 100%;
  /* margin-top: 8px; */
  ${flexBoxCenter}
  flex-direction: column;
`;

const SwitchTitlesWrapper = styled.div`
  height: 24px;
  width: 100%;
  ${flexBoxCenter}
  gap:22.5px;
  position: relative;
`;

const SortedUOSGroupWrapper = styled.div`
  width: 814px;
  height: auto;
  padding: 2px;
  margin-bottom: 6px;

  ${layerA180Deg}

  border-radius: 16px;
`;

const SwitchesBaseLayer = styled.div`
  width: 796px;
  height: 26px;
  margin-top: 2px;

  ${layerADark}

  border-radius: 14px;
  opacity: 1;
  ${flexBoxCenter}

  ${({ isGrouped }) =>
    isGrouped &&
    css`
      width: 750px;
      height: 26px;
    `}
`;

const SwitchesWrapper = styled.div`
  width: 794px;
  height: 24px;

  ${layerA180Deg}

  border-radius: 17px;

  ${justifyContentSpaceAround}

  ${({ isGrouped }) =>
    isGrouped &&
    css`
      width: 748px;
      height: 24px;
      /* ${flexBoxCenter}
      gap:1.7px; */
    `}
`;

const UOSDisplay = styled.div`
  width: 32px;
  height: 18px;
  margin-top: 6px;

  font-size: 10px;

  ${layerCLighter}
  border-radius: 12px;
  ${flexBoxCenter}
`;

const SwitchesInputBox = styled.input`
  height: 17px;
  ${({ switchName, systemId, application, gasType }) =>
    switchName
      ? css`
          width: 136px;
          ${({ isGrouped }) =>
            isGrouped &&
            css`
              margin-left: 2px;
            `}
        `
      : systemId
        ? css`
            width: 148px;
          `
        : application
          ? css`
              width: 86px;
              margin-right: 2px;
            `
          : gasType &&
            css`
              width: 42px;
            `}

  font-size:10px;
  letter-spacing: 1px;
  text-transform: uppercase;

  ${layerA}

  border-radius: 12px;
`;

// const SwitchesDropBoxBaseLayer = styled.div`
//   ${({ isUOS, isSwitchSize, isHeatingSys, isSelectedSSR, isSSRRating }) =>
//     isUOS
//       ? css`
//           width: 44px;
//           height: 18px;
//         `
//       : isSwitchSize
//       ? css`
//           width: 72px;
//           height: 18px;
//         `
//       : isHeatingSys
//       ? css`
//           width: 84px;
//           height: 18px;
//         `
//       : isSelectedSSR
//       ? css`
//           width: 64px;
//           height: 18px;
//         `
//       : isSSRRating &&
//         css`
//           width: 96px;
//           height: 18px;
//         `}

//   ${layerA}

//   border-radius: 12px;
//   opacity: 1;
//   position: relative;
//   ${flexBoxCenter}
// `;

// const SwitchesDropBoxWrapper = styled.div`
//   ${({ isUOS, isSwitchSize, isHeatingSys, isSelectedSSR, isSSRRating }) =>
//     isUOS
//       ? css`
//           width: 42px;
//           height: 16px;
//         `
//       : isSwitchSize
//       ? css`
//           width: 70px;
//           height: 16px;
//         `
//       : isHeatingSys
//       ? css`
//           width: 82px;
//           height: 16px;
//         `
//       : isSelectedSSR
//       ? css`
//           width: 62px;
//           height: 16px;
//         `
//       : isSSRRating &&
//         css`
//           width: 94px;
//           height: 16px;
//         `};

//   ${layerA180Deg}

//   border-radius: 12px;
//   opacity: 1;
//   ${justifyContentSpaceBetween}
// `;

// const SwitchesDisplayInfo = styled.div`
//   ${({ isUOS, isSwitchSize, isHeatingSys, isSelectedSSR, isSSRRating }) =>
//     isUOS
//       ? css`
//           width: 24px;
//           height: 12px;
//         `
//       : isSwitchSize
//       ? css`
//           width: 54px;
//           height: 12px;
//         `
//       : isHeatingSys
//       ? css`
//           width: 64px;
//           height: 12px;
//         `
//       : isSelectedSSR
//       ? css`
//           width: 46px;
//           height: 12px;
//         `
//       : isSSRRating &&
//         css`
//           width: 78px;
//           height: 12px;
//         `};

//   margin-left: 2px;

//   ${layerA}

//   border-radius: 12px;
//   opacity: 1;

//   letter-spacing: 1px;
//   font-size: 10px;
//   ${flexBoxCenter}
// `;

const XButtonWrapper = styled.div`
  width: 18px;
  height: 18px;
  margin-left: 2px;

  ${layerA}

  border-radius: 9px;
  opacity: 1;
  ${flexBoxCenter}

  ${({ isGreenBorder }) =>
    isGreenBorder &&
    css`
      border: 1px solid #95ff45;
    `}
`;

const XButton = styled.div`
  width: 16px;
  height: 16px;

  ${layerA180Deg}

  border-radius: 9px;
  opacity: 1;
  ${flexBoxCenter}
`;

const XButtonTop = styled.div`
  width: 13px;
  height: 13px;

  ${layerB}

  border-radius: 9px;
  opacity: 1;
  ${flexBoxCenter}
`;

const MessageBoxWrapper = styled.div`
  ${({ isLocationName }) =>
    isLocationName
      ? css`
          position: absolute;
          z-index: 10;
          width: auto;
          height: auto;
        `
      : css`
          position: absolute;
          z-index: 10;
          top: 30%;
          right: 10%;
          width: auto;
          height: auto;
        `}
`;

const SSRElementsWrapper = styled.div``;

// const SSRAndTCWrapper = styled.div`
//   width: 118px;
//   height: 20px;

//   ${layerCLighter}
//   border-radius: 12px;
//   ${justifyContentSpaceAround}
// `;

// const SSR = styled.span`
//   font-size: 10px;
//   color: #fcff01;
// `;

// const TCDropBox = styled.div`
//   width: 60px;
//   height: 18px;

//   ${layerA180Deg}
//   border-radius: 12px;
//   ${justifyContentSpaceAround}
// `;

// const TC = styled.div`
//   width: 43px;
//   height: 14px;
//   font-size: 8px;
//   letter-spacing: 0.8px;

//   ${layerA}
//   border-radius: 12px;
// `;

const DisplayUOSName = styled.div`
  width: 287px;
  min-height: 29px;

  background: #233a54 0% 0% no-repeat padding-box;
  box-shadow: inset 0px 0px 6px #000000;
  border-radius: 15px;
  ${flexBoxCenter}
`;

const UOSNameP = styled.p`
  width: 93%;
  font-size: 10px;

  letter-spacing: 1px;
`;

const HeatLoadButtonWrapper = styled.div`
  width: 221px;
  height: 29px;

  ${layerB}
  border-radius: 27px;
  ${flexBoxCenter}

  ${({ isApply }) =>
    isApply &&
    css`
      width: 514px;
      height: 29px;
    `}
`;

const HeatLoadButton = styled.button`
  width: 219px;
  height: 27px;

  ${layerA180Deg}
  border-radius: 25px;
  ${flexBoxCenter}

  ${({ isApply }) =>
    isApply &&
    css`
      width: 512px;
      height: 27px;
    `}
`;

const HeatLoadButtonIndent = styled.div`
  width: 213px;
  height: 21px;

  ${layerB}

  border-radius: 20px;
  ${flexBoxCenter}

  ${({ isApply }) =>
    isApply &&
    css`
      width: 506px;
      height: 21px;
    `}
`;

const HeatLoadButtonTop = styled.div`
  width: 211px;
  height: 19px;

  ${layerA180Deg}

  border-radius: 25px;
  ${flexBoxCenter}

  ${({ isApply }) =>
    isApply &&
    css`
      width: 504px;
      height: 19px;
    `}
`;

const HeatLoadSpan = styled.span`
  font-size: 10px;
`;
